import {
  Avatar,
  Button,
  Center,
  CloseButton,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  TextInput,
  Combobox,
  InputBase,
  Input,
  useCombobox,
  CheckIcon,
  Loader,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AiInfoItemWithMembers,
  MembersItem,
  DatasetItemWithMembers,
  ShareSource,
} from '@mygpt/app/collaborate/services';
import { useGetUserByEmail, User } from '@mygpt/app/users';
import { useMembersMethods } from '@mygpt/app/collaborate';
import { setResourcePrinciple, getResourcesPrinciple, ResourceDataType } from 'packages/app/apis/services/admin';

import LocalMemberSelector from './LocalMemberSelector';
import { useAdminStore } from '@/store/admin';
import { useGPTStore } from '@/store';

interface UserSelectorModalProps {
  opened: boolean;
  onClose: () => void;
  selectedItem: AiInfoItemWithMembers | DatasetItemWithMembers | null;
  type: 'robot' | 'dataset';
  onRefresh: () => void;
  shareSource?: ShareSource;
}

const UserSelectorModal = ({ opened, onClose, selectedItem, type, onRefresh, shareSource }: UserSelectorModalProps) => {
  const { t } = useTranslation();
  const { userInfo } = useGPTStore((state) => state);
  const { isDataReady, fetchAllAdminData } = useAdminStore();

  const [addInputValue, setAddInputValue] = useState('');
  const [originMemberList, setOriginMemberList] = useState<MembersItem[]>([]);
  const [addMemberList, setAddMemberList] = useState<User[]>([]);
  const [deleteMemberList, setDeleteMemberList] = useState<string[]>([]);
  // 组织模式下 Tab 切换:enterprise=企业成员 / personal=个人协作者
  const [orgActiveTab, setOrgActiveTab] = useState<'enterprise' | 'personal'>('enterprise');
  // 组织模式下,从 selectedItem.members 中拆出的企业协作者(仅用于过滤个人列表与邮箱校验)
  const [enterpriseMemberList, setEnterpriseMemberList] = useState<MembersItem[]>([]);

  const { getUserByEmail, loading: addLoading } = useGetUserByEmail();
  const { addMembers, deleteMembers, loading: editLoading } = useMembersMethods();

  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [defaultSelectedUserIds, setDefaultSelectedUserIds] = useState<string[]>([]);
  const [modalLoading, setModalLoading] = useState<boolean>(false);

  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  useEffect(() => {
    if (!opened || !selectedItem) return;
    if (isLocalMode) {
      setOriginMemberList([...selectedItem.members]);
      handleOpenLocalModal();
    } else if (shareSource === 'organization') {
      // 组织模式下不在此处直接灌入 originMemberList,等下方拆分 effect 算出个人协作者再写入
      setOrgActiveTab('enterprise');
      handleOpenLocalModal();
    } else {
      setOriginMemberList([...selectedItem.members]);
    }
  }, [opened, selectedItem]);

  const checkEmailExisted = (email: string) => {
    return (
      addMemberList.find((item) => item.email === email) || originMemberList.find((item) => item.member.email === email)
    );
  };

  const handleAdd = async () => {
    const value = addInputValue.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      showNotification({
        message: t('access.modal.validate.email_error'),
        color: 'red',
      });
      return;
    }
    if (value === userInfo?.email) {
      showNotification({
        message: t('access.modal.validate.self_email'),
        color: 'yellow',
      });
      return;
    }
    if (checkEmailExisted(value)) {
      showNotification({
        message: t('access.modal.validate.email_exist'),
        color: 'yellow',
      });
      return;
    }
    // 组织模式下:如果该邮箱已经在企业协作者中,引导用户到企业成员 Tab 管理
    if (shareSource === 'organization' && enterpriseMemberList.some((item) => item.member.email === value)) {
      showNotification({
        message: t('access.modal.validate.email_in_enterprise'),
        color: 'yellow',
      });
      return;
    }
    const res = await getUserByEmail({ email: value });
    if (res.data.length > 0) {
      if (res.data.length === 1) {
        setAddMemberList([...addMemberList, res.data[0]]);
      } else {
        setUserOptions(res.data);
      }
    } else {
      showNotification({
        message: t('access.modal.validate.user_not_exist'),
        color: 'yellow',
      });
      return;
    }
    setAddInputValue('');
  };

  const manageMembersByType = async (ids: string[], action: 'add' | 'delete', gbaseUserIds?: string[]) => {
    if (!selectedItem) {
      console.error('selectedItem is null');
      return;
    }
    const isRobot = type === 'robot';
    const actionMethod = action === 'add' ? addMembers : deleteMembers;

    if (isRobot && 'ai' in selectedItem) {
      await actionMethod({
        ai_id: selectedItem.ai.id,
        user_ids: ids,
        ai_type: type,
        ...(gbaseUserIds?.length ? { gbase_user_ids: gbaseUserIds } : {}),
      });
    } else if (!isRobot && 'dataset' in selectedItem) {
      await actionMethod({
        resource_id: selectedItem.dataset.id,
        user_ids: ids,
        resource_type: type,
        ...(gbaseUserIds?.length ? { gbase_user_ids: gbaseUserIds } : {}),
      });
    } else {
      console.error('selectedItem does not match the type');
    }
  };

  const handleConfirm = async () => {
    if (!selectedItem) return;

    const addMemberIds = addMemberList.map((item) => item.user_id);
    const { deletedIds, newAddUserIds } = addMemberIds.reduce(
      (acc: { newAddUserIds: string[]; deletedIds: string[] }, id) => {
        const isExisting = selectedItem.members.some((i) => i.member.user_id === id);

        if (!isExisting) {
          acc.newAddUserIds.push(id);
        } else {
          acc.deletedIds.push(id);
        }

        return acc;
      },
      { newAddUserIds: [], deletedIds: [] }
    );
    const newDeleteMemberIds = deleteMemberList.filter((id) => !deletedIds.includes(id));

    if (newAddUserIds.length > 0) {
      await manageMembersByType(newAddUserIds, 'add');
    }
    if (newDeleteMemberIds.length > 0) {
      await manageMembersByType(newDeleteMemberIds, 'delete');
    }
    handleClose();
    onRefresh();
  };

  const handleLocalConfirm = async (users: any[]) => {
    const userIds = users.map((u) => u.id);
    const departmentIds: string[] = [];
    let dataId = '';
    let dataType: ResourceDataType | '' = '';
    let authorityCode = '';
    if (selectedItem) {
      if (type === 'robot' && 'ai' in selectedItem) {
        dataId = selectedItem.ai.id;
        dataType = 'BOT';
        authorityCode = 'BOT_EDIT';
      } else if (type === 'dataset' && 'dataset' in selectedItem) {
        dataId = selectedItem.dataset.id;
        dataType = 'DATASET';
        authorityCode = 'DATASET_EDIT';
      }
    }
    await setResourcePrinciple({
      userIds,
      departmentIds,
      companyAuthorized: false,
      dataId,
      dataType: dataType as ResourceDataType,
      authorityCode,
    });
    handleClose();
    onRefresh();
  };

  const handleOpenLocalModal = async () => {
    setModalLoading(true);
    try {
      const { isDataReady, isDataLoading } = useAdminStore.getState();

      if (!isDataReady && !isDataLoading) {
        console.log('[UserSelectorModal] Data not ready, fetching admin data...');
        await fetchAllAdminData();
      } else if (isDataLoading) {
        console.log('[UserSelectorModal] Data is loading, waiting...');
        await new Promise<void>((resolve) => {
          const checkDataReady = () => {
            const { isDataReady, isDataLoading } = useAdminStore.getState();
            if (isDataReady && !isDataLoading) {
              resolve();
            } else {
              setTimeout(checkDataReady, 100);
            }
          };
          checkDataReady();
        });
      }

      let dataId = '';
      let dataType: ResourceDataType | '' = '';
      let authorityCode = '';

      if (selectedItem) {
        if (type === 'robot' && 'ai' in selectedItem) {
          dataId = selectedItem.ai.id;
          dataType = 'BOT';
          authorityCode = 'BOT_EDIT';
        } else if (type === 'dataset' && 'dataset' in selectedItem) {
          dataId = selectedItem.dataset.id;
          dataType = 'DATASET';
          authorityCode = 'DATASET_EDIT';
        }
      }

      if (dataId && dataType && authorityCode) {
        try {
          const res = await getResourcesPrinciple({
            dataIds: [dataId],
            dataType: dataType as ResourceDataType,
            authorityCode,
          });
          const data = res.data || res;
          const userIds = (data.resourceDataIdToUserIdsMap && data.resourceDataIdToUserIdsMap[dataId]) || [];
          const stringUserIds = userIds.map((id: any) => String(id));
          setDefaultSelectedUserIds(stringUserIds);
        } catch (e) {
          setDefaultSelectedUserIds([]);
        }
      } else {
        setDefaultSelectedUserIds([]);
      }
    } catch (error) {
      console.error('Failed to open local modal:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setAddInputValue('');
    setAddMemberList([]);
    setDeleteMemberList([]);
    setOriginMemberList([]);
    setUserOptions([]);
    setSelectedUser(null);
    setDefaultSelectedUserIds([]);
    setModalLoading(false);
    setEnterpriseMemberList([]);
    setOrgActiveTab('enterprise');
  };

  // 从管理后台 store 获取所有用户（用于 ID-UUID 映射）
  const getAllUsersFromAdminStore = () => {
    const { departmentTree = [], noneDepartmentUsers = [] } = useAdminStore.getState();
    const collect = (tree: any[]): any[] => {
      let result: any[] = [];
      tree.forEach((dept: any) => {
        if (dept.members) result = result.concat(dept.members);
        if (dept.children?.length) result = result.concat(collect(dept.children));
      });
      return result;
    };
    return collect(departmentTree).concat(Array.isArray(noneDepartmentUsers) ? noneDepartmentUsers : []);
  };

  // 组织模式下,等 OGS 数据 (defaultSelectedUserIds) 加载完成后,
  // 将 selectedItem.members 拆成"企业协作者"(OGS principle 命中) 与"个人协作者"(其余)
  useEffect(() => {
    if (!opened || !selectedItem || shareSource !== 'organization') return;
    if (!isDataReady || modalLoading) return;

    const allStoreUsers = getAllUsersFromAdminStore();
    const enterpriseUuids = new Set(
      defaultSelectedUserIds
        .map((id) => allStoreUsers.find((u: any) => String(u.id) === id)?.uuid)
        .filter(Boolean) as string[]
    );

    const enterprise: MembersItem[] = [];
    const personal: MembersItem[] = [];
    selectedItem.members.forEach((m) => {
      if (enterpriseUuids.has(m.member.user_id)) {
        enterprise.push(m);
      } else {
        personal.push(m);
      }
    });
    setEnterpriseMemberList(enterprise);
    setOriginMemberList(personal);
    setAddMemberList([]);
    setDeleteMemberList([]);
  }, [defaultSelectedUserIds, isDataReady, modalLoading, opened, selectedItem, shareSource]);

  // 组织内协作确认：diff 选中变化后调用 add/delete 接口，并复用 handleLocalConfirm 同步 OGS 资源权限
  const handleOrgConfirm = async (users: any[]) => {
    if (!selectedItem) return;
    const newUserIds = users.map((u: any) => String(u.id));
    const currentUserIds = defaultSelectedUserIds;

    const toAdd = newUserIds.filter((id: string) => !currentUserIds.includes(id));
    const toDelete = currentUserIds.filter((id: string) => !newUserIds.includes(id));

    // 提取新增用户的 uuid 作为 gbase_user_ids
    const toAddGbaseUserIds = toAdd
      .map((id: string) => users.find((u: any) => String(u.id) === id)?.uuid)
      .filter(Boolean) as string[];

    // 删除时也提取 uuid
    const allStoreUsers = getAllUsersFromAdminStore();
    const toDeleteGbaseUserIds = toDelete
      .map((id: string) => allStoreUsers.find((u: any) => String(u.id) === id)?.uuid)
      .filter(Boolean) as string[];

    if (toAdd.length > 0) await manageMembersByType(toAdd, 'add', toAddGbaseUserIds);
    if (toDelete.length > 0) await manageMembersByType(toDelete, 'delete', toDeleteGbaseUserIds);

    // 复用 handleLocalConfirm 同步 OGS 资源权限 + 关闭弹窗 + 刷新列表
    await handleLocalConfirm(users);
  };

  const noCollaborator = originMemberList.length === 0 && addMemberList.length === 0;

  const MembersItem = ({ email, name, onRemove }: { name: string; email: string; onRemove: () => void }) => {
    return (
      <Group justify="space-between" className="p-3">
        <Group>
          <Text size="sm">{name}</Text>
          <Text size="sm" c="gray.4">
            {email}
          </Text>
        </Group>
        <Text size="sm" c="violet.7" className="cursor-pointer" onClick={onRemove}>
          {t('access.edit_member.remove')}
        </Text>
      </Group>
    );
  };

  // 个人协作者管理 UI(邮箱搜索+成员列表+保存),既用于个人模式,也复用为组织模式下的"个人协作者" Tab
  const personalSection = (
    <Stack>
      <h3 className="text-base font-bold">{t('access.edit_member.title1')}</h3>
      <div className="text-sm">{t('access.edit_member.desc1')}</div>
      <Group>
        <TextInput
          className="flex-1"
          placeholder={t('access.modal.placeholder')}
          value={addInputValue}
          onChange={(e) => {
            setAddInputValue(e.target.value);
          }}
          rightSection={
            <CloseButton
              aria-label="Clear input"
              onClick={() => setAddInputValue('')}
              style={{ display: addInputValue ? undefined : 'none' }}
            />
          }
        />
        <Button onClick={handleAdd} loading={addLoading}>
          {t('access.edit_member.add_btn')}
        </Button>
      </Group>

      {userOptions.length > 1 && (
        <>
          <Combobox
            store={combobox}
            onOptionSubmit={(value) => {
              const user = userOptions.find((u) => u.user_id === value);
              if (user) {
                setSelectedUser(user);
              }
              combobox.closeDropdown();
            }}
          >
            <Combobox.Target>
              <InputBase
                component="button"
                type="button"
                pointer
                rightSection={<Combobox.Chevron />}
                rightSectionPointerEvents="none"
                onClick={() => combobox.toggleDropdown()}
              >
                {selectedUser?.email || <Input.Placeholder>{t('access.edit_member.select_user')}</Input.Placeholder>}
              </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown>
              <Combobox.Options>
                {userOptions.map((item) => (
                  <Combobox.Option value={item.user_id} key={item.user_id}>
                    <Group>
                      <Avatar key={item.user_id} src={item.picture} radius="xl" size="md" />
                      <Stack justify="flex-start" gap={0}>
                        <Text>{item.name}</Text>
                        <Text size="sm" c="gray.4">
                          {item.email}
                        </Text>
                      </Stack>
                      {item.user_id === selectedUser?.user_id && <CheckIcon size={12} />}
                    </Group>
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
          <Button
            onClick={() => {
              if (selectedUser) {
                setAddMemberList([...addMemberList, selectedUser]);
                setUserOptions([]);
                setSelectedUser(null);
              }
            }}
          >
            {t('access.edit_member.add_btn')}
          </Button>
        </>
      )}

      <h3 className="text-base font-bold">{t('access.edit_member.title2')}</h3>

      {noCollaborator ? (
        <div className="bg-gray-100 py-10 px-3 rounded-md">
          <Text c="gray.5" size="sm" className="text-center">
            {t('access.edit_member.no_member_tip1')}
          </Text>
          <Text c="gray.5" size="sm" className="text-center">
            {t('access.edit_member.no_member_tip2')}
          </Text>
        </div>
      ) : (
        <ScrollArea.Autosize mah={320} className="border rounded-md">
          {originMemberList.map((item, index) => {
            return (
              <MembersItem
                key={item.id}
                name={item.member.name}
                email={item.member.email}
                onRemove={() => {
                  setDeleteMemberList([...deleteMemberList, item.member.user_id]);
                  setOriginMemberList(originMemberList.filter((_, i) => i !== index));
                }}
              />
            );
          })}
          {addMemberList.map((item, index) => {
            return (
              <MembersItem
                key={item.user_id}
                name={item.name}
                email={item.email}
                onRemove={() => {
                  setAddMemberList(addMemberList.filter((_, i) => i !== index));
                }}
              />
            );
          })}
        </ScrollArea.Autosize>
      )}

      <Group justify="flex-end" mt={20}>
        <Button variant="outline" color="gray" onClick={handleClose}>
          {t('alert_cancel')}
        </Button>
        <Button onClick={handleConfirm} loading={editLoading}>
          {t('save')}
        </Button>
      </Group>
    </Stack>
  );

  // 组织模式:外层 Modal 内通过 Tabs 在"企业成员"和"个人协作者"两种管理形态之间切换
  if (shareSource === 'organization') {
    return (
      <Modal
        centered
        opened={opened}
        onClose={handleClose}
        size="xl"
        title={t('access.edit_member.modal_title')}
        withCloseButton
      >
        {!isDataReady || modalLoading ? (
          <Center style={{ minHeight: 400 }}>
            <Loader size="lg" />
          </Center>
        ) : (
          <Tabs value={orgActiveTab} onChange={(v) => v && setOrgActiveTab(v as 'enterprise' | 'personal')}>
            <Tabs.List mb="md">
              <Tabs.Tab value="enterprise">{t('access.edit_member.tab.enterprise')}</Tabs.Tab>
              <Tabs.Tab value="personal">{t('access.edit_member.tab.personal')}</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="enterprise">
              <LocalMemberSelector
                embedded
                open={opened}
                onClose={handleClose}
                onConfirm={handleOrgConfirm}
                defaultSelectedUserIds={defaultSelectedUserIds}
                isUserSelectable={(user: any) => {
                  // 排除查看者角色用户,与本地部署逻辑一致
                  const roles = user.roles;
                  if (Array.isArray(roles) && roles.length > 0 && roles.every((r: any) => r.code === 'ROLE_VIEWER')) {
                    return false;
                  }
                  return true;
                }}
              />
            </Tabs.Panel>
            <Tabs.Panel value="personal">{personalSection}</Tabs.Panel>
          </Tabs>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      size="lg"
      title={t('access.edit_member.modal_title')}
      withCloseButton
    >
      {isLocalMode ? (
        !isDataReady || modalLoading ? (
          <Center style={{ minHeight: 300 }}>
            <Loader size="lg" />
          </Center>
        ) : (
          <LocalMemberSelector
            open={opened}
            onClose={handleClose}
            onConfirm={handleLocalConfirm}
            defaultSelectedUserIds={defaultSelectedUserIds}
          />
        )
      ) : (
        personalSection
      )}
    </Modal>
  );
};

export default UserSelectorModal;
